// Package Imports
import React from 'react';
import { Table, Space, Tooltip, Empty } from 'antd';
import {
  ClockCircleFilled,
  MinusOutlined,
  UserOutlined,
  RiseOutlined,
  BellOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import WorkflowStatusTableProps from 'pages/workflow/interfaces/WorkflowStatusTableProps';
import ExtendedStageData from 'pages/workflow/interfaces/ExtendedStageData';
import MetaLineConfig from 'pages/workflow/interfaces/MetaLineConfig';
import STATUSTOKENS from 'pages/workflow/utils';
import CompletedCell from 'pages/workflow/CompletedCell';

const DEFAULTTOKEN = {
  classes: 'text-slate-500 bg-slate-100',
  icon: <ClockCircleFilled />,
  fallbackLabel: '—',
};

const BADGEBASE =
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold leading-5 whitespace-nowrap';

const pick = (obj: any, keys: string[]): any => {
  if (!obj || typeof obj !== 'object') return undefined;
  // Find the first key that meets your criteria
  const foundKey = keys.find(
    (key) => obj[key] !== undefined && obj[key] !== null && obj[key] !== ''
  );
  // Return the value using foundKey, otherwise return undefined
  return foundKey !== undefined ? obj[foundKey] : undefined;
};

const isTruthy = (val: any): boolean =>
  val === true || val === 'true' || val === 1;

const formatAnyDate = (value?: string | null): string | null => {
  if (!value) return null;
  let parsed = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
  if (!parsed.isValid()) parsed = dayjs(value); // falls back to ISO parsing
  return parsed.isValid() ? parsed.format('DD MMM, HH:mm') : null;
};

const titleCaseFreq = (freq?: string | null): string | null => {
  if (!freq) return null;
  const map: Record<string, string> = {
    DAY: 'daily',
    WEEK: 'weekly',
    MONTH: 'monthly',
  };
  return map[freq.toUpperCase()] || freq.toLowerCase();
};

const MetaLine = ({
  meta,
  activeKeys,
  label,
  icon,
  iconColorClass,
  toKeys,
  toPrefix,
}: MetaLineConfig) => {
  if (!meta || typeof meta !== 'object') return null;
  if (!isTruthy(pick(meta, activeKeys))) return null;

  const frequency = titleCaseFreq(pick(meta, ['frequency']));
  const to = toKeys ? pick(meta, toKeys) : undefined;
  const roleName = pick(meta, ['role_name', 'roleName']);
  const executionDate = formatAnyDate(
    pick(meta, ['executionDate', 'execution_date'])
  );

  const tooltipLines = [
    roleName ? `Role: ${roleName}` : null,
    executionDate ? `Next run: ${executionDate}` : null,
    to ? `${toPrefix ?? 'To'}: ${to}` : null,
  ].filter(Boolean);

  const suffix = to ?? frequency;

  return (
    <Tooltip
      title={
        tooltipLines.length ? (
          <Space direction="vertical" size={2}>
            {tooltipLines.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </Space>
        ) : null
      }
    >
      <div className="flex items-center justify-center gap-1.5 text-[11.5px] text-gray-600 leading-5">
        <span className={`inline-flex text-[13px] ${iconColorClass}`}>
          {icon}
        </span>
        <span className="font-medium text-gray-700">{label}</span>
        {suffix ? (
          <span className="text-gray-400">&middot; {suffix}</span>
        ) : null}
      </div>
    </Tooltip>
  );
};

const MetaSection = ({
  stageData,
}: {
  stageData: ExtendedStageData | null | undefined;
}) => {
  if (!stageData) return null;

  const lines = [
    {
      meta: stageData.escalation,
      activeKeys: ['is_escalated', 'isEscalated'],
      label: 'Escalated',
      icon: <RiseOutlined />,
      iconColorClass: 'text-rose-500',
      toKeys: ['escalated_to', 'escalatedTo'],
      toPrefix: 'To',
    },
    {
      meta: stageData.reminder,
      activeKeys: ['is_reminder', 'isReminder'],
      label: 'Reminder set',
      icon: <BellOutlined />,
      iconColorClass: 'text-sky-500',
      toKeys: ['reminder_to', 'reminderTo'],
      toPrefix: 'To',
    },
    {
      meta: stageData.auto_approval,
      activeKeys: ['is_auto_approval', 'isAutoApproval'],
      label: 'Auto-approves',
      icon: <ThunderboltOutlined />,
      iconColorClass: 'text-violet-500',
    },
  ];

  const hasAny = lines.some(
    (l) => l.meta && isTruthy(pick(l.meta, l.activeKeys))
  );
  if (!hasAny) return null;

  return (
    <div className="w-full">
      <div className="border-t border-gray-100 my-1.5" />
      <div className="flex flex-col gap-1">
        {lines.map((line, i) => (
          <MetaLine key={i} {...line} />
        ))}
      </div>
    </div>
  );
};

const StatusBadge = ({
  stageData,
}: {
  stageData: ExtendedStageData | null | undefined;
}) => {
  if (!stageData) {
    return (
      <span className="text-gray-300">
        <MinusOutlined />
      </span>
    );
  }

  const status = stageData.status?.toUpperCase() ?? '';
  const token = STATUSTOKENS[status] ?? DEFAULTTOKEN;
  const label = stageData.comment || token.fallbackLabel || status || '—';
  const formattedDate = stageData.date
    ? dayjs(stageData.date, 'YYYY-MM-DD HH:mm:ss').format('DD MMM, HH:mm')
    : null;

  return (
    <div className="flex flex-col items-center gap-1 w-full py-1">
      <div className={`${BADGEBASE} ${token.classes}`}>
        <span className="inline-flex text-[11px]">{token.icon}</span>
        <span>{label}</span>
      </div>
      {formattedDate ? (
        <div className="text-[11px] text-gray-400">{formattedDate}</div>
      ) : null}
      {stageData.approvedBy ? (
        <div className="flex items-center gap-1 text-[11px] text-gray-400">
          <UserOutlined />
          {stageData.approvedBy}
        </div>
      ) : null}

      <MetaSection stageData={stageData} />
    </div>
  );
};

const WorkflowStatusTable = ({
  tableProps,
  headers,
  loading,
}: WorkflowStatusTableProps) => {
  if (!headers?.length) return null;

  const columns = headers.map((header) => {
    if (header.type === 'text') {
      return {
        title: header.title,
        dataIndex: header.dataIndex,
        key: header.dataIndex,
        fixed: header.dataIndex === 'name' ? ('left' as const) : undefined,
        width: header.dataIndex === 'name' ? 200 : undefined,
        render: (text: string, record: any) => {
          if (header.dataIndex === 'name') {
            return (
              <Space direction="vertical" size={0}>
                <span className="text-[13.5px] font-semibold text-gray-800">
                  {text}
                </span>
                {record.qualifier ? (
                  <span className="text-[11.5px] text-gray-400">
                    {record.qualifier}
                  </span>
                ) : null}
              </Space>
            );
          }
          return (
            <span className="text-[13px] text-gray-700">{text ?? '—'}</span>
          );
        },
      };
    }

    // Completed column
    if (header.dataIndex === 'completed') {
      return {
        title: header.title,
        dataIndex: header.dataIndex,
        key: header.dataIndex,
        width: 120,
        align: 'center' as const,
        render: (value: boolean) => (
          <div className="flex justify-center">
            <CompletedCell value={value} />
          </div>
        ),
      };
    }
    return {
      title: header.title,
      dataIndex: header.dataIndex,
      key: header.dataIndex,
      width: 220,
      align: 'center' as const,
      render: (stageData: ExtendedStageData) => (
        <div className="flex justify-center">
          <StatusBadge stageData={stageData} />
        </div>
      ),
    };
  });

  return (
    <div>
      <Table
        {...tableProps}
        columns={columns}
        loading={loading}
        rowKey={(record: any) => record.unique_id || record.qualifier}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...(tableProps?.pagination ?? {}),
          size: 'default',
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No workflow activity yet"
              className="py-8"
            />
          ),
        }}
      />
    </div>
  );
};

export default WorkflowStatusTable;
